export interface AssetLocation {
  key: string;
  imagePath: string;
  jsonPath: string;
}

export interface CatAssetMap {
  body: AssetLocation;
  hand_f: AssetLocation;
  hand_b: AssetLocation;
  leg_b: AssetLocation;
  leg_f: AssetLocation;
  tail: AssetLocation;
  // Note: Eyes are not included as they don't exist as separate atlas files
}

export type AccessoryType = "clothes" | "hat" | "glasses";
export type Animation =
  | "dead"
  | "fly"
  | "hit"
  | "idle"
  | "jump"
  | "roll"
  | "stunned"
  | "throwing"
  | "walk";

// Default animation should be idle
export const DEFAULT_ANIMATION: Animation = "idle";

// Animation effects mapping
export const ANIMATION_EFFECTS: { [key in Animation]?: string } = {
  hit: "hammer",
  fly: "umbrella",
  stunned: "stunned",
  throwing: "throwing",
};

// Map canonical animation names to legacy on-disk folder names
const ANIMATION_DISK_ALIASES: Record<string, string> = {
  stunned: "stuned",
};

export class AssetLocator {
  private static readonly MAX_CATS = 15;

  /**
   * Clothes whose atlases are shipped as a single layer (no separate front / back files).
   * We keep this list central so both getAccessoryAsset() and getClothesAssets() stay in sync.
   */
  private static readonly SINGLE_LAYER_CLOTHES = [
    // Original
    "aqua_shirt",
    "red_pants",
    "skull_suit",
    "necklace",
    // Newly added
    "amber_shirt",
    "orange_shirt",
    "pink_shirt",
    "brown_belt",
    "brown_pants_01",
    "brown_pants_02",
    "red_blue_pants",
    "teal_pants",
  ];

  /**
   * Some accessories live in their own folder but the PNG / JSON files use a different prefix.
   * This map translates the logical accessory name to the actual file prefix on disk.
   */
  private static readonly ACCESSORY_FILE_PREFIX_OVERRIDES: Record<
    string,
    string
  > = {
    // Hats
    blue_cap: "blue_hat",
    blue_feathers: "blue_hat_02",
    // Clothes
    amber_shirt: "yellow_shirt",
    red_blue_pants: "red_pants",
    brown_pants_01: "brown_pants",
    teal_pants: "pants_01",
  };

  // Asset sharing rules based on documentation.txt
  private static readonly SHARED_ASSETS = {
    body: {
      // Some cats reuse body atlases of others (see documentation.txt)
      // ch02 assets were not shipped – reuse ch05 which has matching silhouette / palette
      "02": "05", // ch02 same as ch05 (fallback – missing on disk)
      // ch07 reuses ch06 body according to docs
      "07": "06", // ch07 same as ch06
      // Existing rules
      "08": "05", // ch08 same as ch05
      "13": "12", // ch13 same as ch12
    },
    hand_f: {
      // Per documentation: ch12, ch13 same as ch11; ch03 same as ch04; ch05, ch08, ch11, ch12, ch13 same as ch02; ch14 same as ch09
      // unique: ch01, ch02, ch03, ch06, ch07, ch09, ch10, ch15
      "04": "03", // ch04 same as ch03 (since ch03 is unique)
      "05": "02", // ch05 same as ch02
      "08": "02", // ch08 same as ch02
      "11": "02", // ch11 same as ch02
      "12": "02", // ch12 same as ch11, ch11 same as ch02 -> ch02
      "13": "02", // ch13 same as ch11, ch11 same as ch02 -> ch02
      "14": "09", // ch14 same as ch09
    },
    hand_b: {
      // Per documentation: ch2, ch5, ch7, ch8, ch11, ch12, ch13, ch15 same as ch1; ch4 same as ch3; ch14 same as ch9
      // unique: ch01, ch03, ch06, ch09, ch10, ch14
      "02": "01", // ch02 same as ch01
      "04": "03", // ch04 same as ch03
      "05": "01", // ch05 same as ch01
      "07": "01", // ch07 same as ch01
      "08": "01", // ch08 same as ch01
      "11": "01", // ch11 same as ch01
      "12": "01", // ch12 same as ch01
      "13": "01", // ch13 same as ch01
      "15": "01", // ch15 same as ch01
    },
    leg_b: {
      // All legs are identical to cat01
      "02": "01",
      "03": "01",
      "04": "01",
      "05": "01",
      "06": "01",
      "07": "01",
      "08": "01",
      "09": "01",
      "10": "01",
      "11": "01",
      "12": "01",
      "13": "01",
      "14": "01",
      "15": "01",
    },
    leg_f: {
      // All legs are identical to cat01
      "02": "01",
      "03": "01",
      "04": "01",
      "05": "01",
      "06": "01",
      "07": "01",
      "08": "01",
      "09": "01",
      "10": "01",
      "11": "01",
      "12": "01",
      "13": "01",
      "14": "01",
      "15": "01",
    },
    tail: {
      // Per documentation: ch03...08, ch10 same as ch02; ch09 same as ch14; ch12 same as ch13
      // unique: ch01, ch02, ch09, ch12, ch15
      "03": "02", // ch03 same as ch02
      "04": "02", // ch04 same as ch02
      "05": "02", // ch05 same as ch02
      "06": "02", // ch06 same as ch02
      "07": "02", // ch07 same as ch02
      "08": "02", // ch08 same as ch02
      "10": "02", // ch10 same as ch02
      "11": "02", // ch11 same as ch02 (not explicitly mentioned, but follows pattern)
      "13": "12", // ch13 same as ch12 (since ch12 is unique)
      "14": "09", // ch14 same as ch09 (ch09 same as ch14, but ch09 is unique)
    },
  };

  constructor(private catNumber: number) {
    if (catNumber < 1 || catNumber > AssetLocator.MAX_CATS) {
      throw new Error(
        `Cat number must be between 1 and ${AssetLocator.MAX_CATS}`,
      );
    }
  }

  /**
   * Gets the effective cat number for a specific body part, accounting for shared assets
   */
  private getEffectiveCatNumber(bodyPart: keyof CatAssetMap): number {
    const catStr = this.catNumber.toString().padStart(2, "0");
    const sharedMapping =
      AssetLocator.SHARED_ASSETS[
        bodyPart as keyof typeof AssetLocator.SHARED_ASSETS
      ];

    if (sharedMapping && sharedMapping[catStr]) {
      return parseInt(sharedMapping[catStr]);
    }

    return this.catNumber;
  }

  /**
   * Gets asset location for a specific body part and animation
   */
  getBodyPartAsset(
    bodyPart: keyof CatAssetMap,
    animation: Animation,
  ): AssetLocation {
    // Translate to on-disk folder name if we have an alias
    const diskAnim = ANIMATION_DISK_ALIASES[animation] ?? animation;

    const effectiveCatNumber = this.getEffectiveCatNumber(bodyPart);
    const catStr = effectiveCatNumber.toString().padStart(2, "0");

    const key = `cat${catStr}_${bodyPart}_${animation}`;
    const basePath = `cats/cat${catStr}/${diskAnim}/cat${catStr}_${bodyPart}_${diskAnim}`;

    return {
      key,
      imagePath: `${basePath}.png`,
      jsonPath: `${basePath}.json`,
    };
  }

  /**
   * Gets all body part assets for a specific animation
   */
  getAllBodyAssets(animation: Animation): CatAssetMap {
    return {
      body: this.getBodyPartAsset("body", animation),
      hand_f: this.getBodyPartAsset("hand_f", animation),
      hand_b: this.getBodyPartAsset("hand_b", animation),
      leg_b: this.getBodyPartAsset("leg_b", animation),
      leg_f: this.getBodyPartAsset("leg_f", animation),
      tail: this.getBodyPartAsset("tail", animation),
    };
  }

  /**
   * Gets accessory asset location
   */
  getAccessoryAsset(
    accessoryType: AccessoryType,
    accessoryName: string,
    animation: Animation,
    layer?: "b" | "f",
  ): AssetLocation {
    const diskAnim = ANIMATION_DISK_ALIASES[animation] ?? animation;
    let key: string;
    let basePath: string;

    // Resolve on-disk file prefix (some assets reuse existing atlases)
    const filePrefix =
      AssetLocator.ACCESSORY_FILE_PREFIX_OVERRIDES[accessoryName] ??
      accessoryName;

    // Clothes shipped as a single-layer atlas
    if (AssetLocator.SINGLE_LAYER_CLOTHES.includes(accessoryName)) {
      // Single-layer clothes use "<prefix>_<animation>.json/png"
      key = `${accessoryName}_${animation}`;
      basePath = `clothes/${accessoryName}/${diskAnim}/${filePrefix}_${diskAnim}`;
    } else {
      // Special case: pirate_bandage lives in clothes folder but is logically a pair of glasses
      if (accessoryType === "glasses" && accessoryName === "pirate_bandage") {
        key = `${accessoryName}_${animation}`;
        basePath = `clothes/${accessoryName}/${diskAnim}/${filePrefix}_${diskAnim}`;
      } else {
        switch (accessoryType) {
          case "clothes":
            if (!layer)
              throw new Error("Clothes accessories require a layer (b or f)");
            const layerName = layer === "b" ? "back" : "front";
            key = `${accessoryName}_${layer}_${animation}`; // Phaser key
            basePath = `clothes/${accessoryName}/${diskAnim}/${filePrefix}_${layerName}_${diskAnim}`;
            break;
          case "hat":
            key = `${accessoryName}_${animation}`;
            basePath = `hats/${accessoryName}/${diskAnim}/${filePrefix}_${diskAnim}`;
            break;
          case "glasses":
            key = `${accessoryName}_${animation}`;
            basePath = `glasses/${accessoryName}/${diskAnim}/${filePrefix}_${diskAnim}`;
            break;
          default:
            throw new Error(`Unknown accessory type: ${accessoryType}`);
        }
      }
    }

    return {
      key,
      imagePath: `${basePath}.png`,
      jsonPath: `${basePath}.json`,
    };
  }

  /**
   * Gets both front and back layers for clothes
   */
  getClothesAssets(
    clothesName: string,
    animation: Animation,
  ): { back: AssetLocation; front: AssetLocation } {
    if (AssetLocator.SINGLE_LAYER_CLOTHES.includes(clothesName)) {
      const sharedAsset = this.getAccessoryAsset(
        "clothes",
        clothesName,
        animation,
        "b",
      );
      return {
        back: sharedAsset,
        front: sharedAsset,
      };
    }

    return {
      back: this.getAccessoryAsset("clothes", clothesName, animation, "b"),
      front: this.getAccessoryAsset("clothes", clothesName, animation, "f"),
    };
  }

  /**
   * Gets all available accessory names for a given type
   */
  static getAvailableAccessories(type: AccessoryType): string[] {
    switch (type) {
      case "clothes":
        return [
          // Single-layer first
          "aqua_shirt",
          "amber_shirt",
          "orange_shirt",
          "pink_shirt",
          "brown_belt",
          "brown_pants_01",
          "brown_pants_02",
          "red_blue_pants",
          "red_pants",
          "skull_suit",
          "teal_pants",
          "necklace",
          // Multi-layer
          "blue_shirt",
          "brown_shirt",
          "yellow_shirt",
        ];
      case "hat":
        return [
          "blue_hat",
          "blue_cap",
          "blue_feathers",
          "brown_hat",
          "crown",
          "orange_hat",
          "pink_hat",
          "viking_helmet",
          "stocking_cap",
          "yellow_hat",
          "pirate_hat",
          "swimming_cap",
        ];
      case "glasses":
        return [
          "brown_glasses",
          "circular_glasses",
          "grey_glasses",
          "orange_glasses",
          "pink_glasses",
          "shutter_glasses",
          "ski_glasses",
          "ski_glasses_pink",
          "square_glasses",
          "pirate_bandage",
        ];
      default:
        return [];
    }
  }

  /**
   * Gets all available animations
   */
  static getAvailableAnimations(): Animation[] {
    return ["fly", "idle", "jump", "stunned", "walk"];
  }

  /**
   * Validates if a cat number is valid
   */
  static isValidCatNumber(catNumber: number): boolean {
    return catNumber >= 1 && catNumber <= AssetLocator.MAX_CATS;
  }

  /**
   * Gets the cat number for this locator
   */
  getCatNumber(): number {
    return this.catNumber;
  }

  /**
   * Gets effect asset for a specific animation
   */
  static getEffectAsset(animation: Animation): AssetLocation | null {
    const diskAnim = ANIMATION_DISK_ALIASES[animation] ?? animation;
    const effectName = ANIMATION_EFFECTS[animation];
    if (!effectName) return null;

    const key = `${effectName}_${animation}`;

    // Map effect names to their actual file paths (checked actual file system)
    let basePath: string;
    switch (effectName) {
      case "hammer":
        // PNG is correctly named "hammer_hit.png" but JSON was shipped as "hummer_hit.json".
        // We standardise both by pointing to the correct base path after renaming the JSON below.
        basePath = `effects/hammer/hammer_hit`;
        break;
      case "umbrella":
        basePath = `effects/umbrella/fly/umbrella_fly`;
        break;
      case "throwing":
        // New art drop nests the atlas one level deeper and prefixes the filename with "splash_".
        basePath = `effects/throwing/throwing/splash_throwing`;
        break;
      case "stunned":
        // Atlas files are named "fx_stuned.*" under the stunned folder.
        basePath = `effects/stunned/fx_stuned`;
        break;
      default:
        return null;
    }

    return {
      key,
      imagePath: `${basePath}.png`,
      jsonPath: `${basePath}.json`,
    };
  }

  /**
   * Checks if an animation has an associated effect
   */
  static hasEffect(animation: Animation): boolean {
    return !!ANIMATION_EFFECTS[animation];
  }
}
